import { useCallback, useEffect, useState } from 'react';
import { Boxes } from 'lucide-react';
import { api, traerTodo } from '../api';
import { useListaUrl, consultaLista } from '../lib/urlEstado';
import ListaEstandar from '../ui/ListaEstandar';
import Paginador from '../ui/Paginador';
import EstadoVacio from '../ui/EstadoVacio';
import BotonIluminar from '../ui/BotonIluminar';
import NotaExplicativa from '../ui/NotaExplicativa';
import { Buscador } from '../ui/Filtros';
import { formatearCantidad } from '../experiencia/cantidades';

// Qué hay, y dónde. Es la vista que faltaba desde B-1: buscar un pasillo
// llevaba a sus etiquetas QR, que es media respuesta — lo que quiere
// saber quien busca "PASILLO-3" es qué mercancía tiene delante.
export default function Existencias({ contexto, irA }) {
  const { filtros, pagina, porPagina, desplazamiento, fijarFiltro, fijarPagina } =
    useListaUrl({
      ubicacion: contexto?.ubicacion || '',
      sku: contexto?.sku || '',
      solo_con_stock: true,
    }, { porPagina: 100 });

  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // La ubicación y el SKU se normalizan a mayúsculas para el servidor,
  // pero en la barra de direcciones se conserva lo que se escribió: un
  // enlace no debe cambiar lo que el otro ve en su propio buscador.
  const paraServidor = useCallback((f) => ({
    ubicacion: f.ubicacion.trim().toUpperCase(),
    sku: f.sku.trim().toUpperCase(),
    solo_con_stock: f.solo_con_stock,
  }), []);

  useEffect(() => {
    let vigente = true;
    const t = setTimeout(() => {
      setCargando(true);
      api.getConTotal(
        `/inventario/existencias?${consultaLista(paraServidor(filtros), {
          limite: porPagina, desplazamiento,
        })}`,
      )
        .then((r) => { if (vigente) { setFilas(r.filas); setTotal(r.total); } })
        .catch((e) => vigente && setError(e.message))
        .finally(() => vigente && setCargando(false));
    }, 250);
    return () => { vigente = false; clearTimeout(t); };
  }, [filtros, porPagina, desplazamiento, paraServidor]);

  const exportarTodo = useCallback(
    (alAvanzar) => traerTodo(
      (limite, desde) => `/inventario/existencias?${consultaLista(paraServidor(filtros), {
        limite, desplazamiento: desde,
      })}`,
      { alAvanzar },
    ),
    [filtros, paraServidor],
  );

  // Recargar tras un fallo, desde «Volver a intentarlo». Repite la consulta
  // del efecto; el efecto conserva su guardia contra respuestas tardías.
  const recargar = useCallback(() => {
    setCargando(true);
    setError(null);
    return api.getConTotal(
      `/inventario/existencias?${consultaLista(paraServidor(filtros), {
        limite: porPagina, desplazamiento,
      })}`,
    )
      .then((r) => { setFilas(r.filas); setTotal(r.total); })
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [filtros, porPagina, desplazamiento, paraServidor]);

  const columnas = [
    { etiqueta: 'SKU', valor: (e) => e.sku, clase: 'font-mono text-xs' },
    { etiqueta: 'Producto', valor: (e) => e.producto, clase: 'font-medium text-gray-900' },
    {
      etiqueta: 'Ubicación',
      valor: (e) => e.ubicacion,
      clase: 'text-gray-600',
      // El código, y junto a él la luz — SOLO si ese estante tiene una
      // tira instalada y respondiendo. Donde no la haya, este renglón se
      // ve exactamente igual que antes.
      render: (e) => (
        <span className="inline-flex items-center gap-2">
          {e.ubicacion}
          <BotonIluminar ubicacion={e.ubicacion} compacto />
        </span>
      ),
    },
    { etiqueta: 'Lote', valor: (e) => e.codigo_lote || '', clase: 'text-gray-500' },
    { etiqueta: 'Caduca', tipo: 'fecha', valor: (e) => e.fecha_caducidad || '', clase: 'text-gray-500' },
    {
      etiqueta: 'Cantidad', alineado: 'derecha',
      valor: (e) => Number(e.cantidad),
      render: (e) => (
        <span className="tabular-nums font-semibold text-gray-900">
          {formatearCantidad(e.cantidad, e.unidad_medida)} <span className="text-xs font-normal text-gray-500">{e.unidad_medida}</span>
        </span>
      ),
    },
    {
      etiqueta: 'Comprometido', alineado: 'derecha',
      valor: (e) => Number(e.comprometido ?? 0),
      render: (e) => (Number(e.comprometido) > 0
        ? <span className="tabular-nums font-semibold text-amber-700">{formatearCantidad(e.comprometido, e.unidad_medida)}</span>
        : <span className="text-gray-500">—</span>),
    },
  ];

  const comprometido = filas.reduce((s, e) => s + Number(e.comprometido || 0), 0);

  return (
    <>
      <ListaEstandar
        titulo="Existencias"
        subtitulo="Qué hay y dónde está, al grano en que se cuenta y se ajusta."
        nota={
          <NotaExplicativa titulo="¿Qué significa “comprometido”?">
            Son unidades que alguien ya pidió sacar y esperan autorización. <strong>No están
            descontadas</strong> del saldo: la mercancía sigue en el estante. Es un aviso, no una
            reserva — si vas a tomar de ahí, mira antes en «Salidas por aprobar» qué se pidió y por qué.
          </NotaExplicativa>
        }
        filtros={
          <>
            <Buscador
              valor={filtros.ubicacion}
              onChange={(v) => fijarFiltro({ ubicacion: v })}
              placeholder="Ubicación exacta…"
            />
            <Buscador
              valor={filtros.sku}
              onChange={(v) => fijarFiltro({ sku: v })}
              placeholder="SKU exacto…"
            />
            <label className="text-sm text-gray-600 flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={filtros.solo_con_stock}
                onChange={(e) => fijarFiltro({ solo_con_stock: e.target.checked })}
              />
              Solo con existencia
            </label>
          </>
        }
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        error={error}
        onReintentar={recargar}
        exportar={{ nombre: 'existencias', columnas, obtenerTodo: exportarTodo }}
        paginador={
          <Paginador
            pagina={pagina}
            porPagina={porPagina}
            total={total}
            mostrados={filas.length}
            cargando={cargando}
            onPagina={fijarPagina}
          />
        }
        claveFila={(e, i) => `${e.sku}-${e.ubicacion}-${e.codigo_lote || ''}-${i}`}
        vacio={
          <EstadoVacio
            icono={Boxes}
            titulo="Nada con existencia aquí"
            descripcion="Prueba a quitar el filtro de ubicación, o desmarca “solo con existencia” para ver también los saldos en cero."
          />
        }
        acciones={(e) => [{ etiqueta: 'Ficha', onClick: () => irA?.('productos', { sku: e.sku }) }]}
      />

      {comprometido > 0 && (
        <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          {/* «En esta página», no «en esta vista»: desde que la lista pagina,
              la suma es la de los renglones que se ven, y decir lo contrario
              sería dar por total lo que es un parcial. */}
          En esta página hay {comprometido} unidad(es) comprometidas en solicitudes que esperan
          autorización. Siguen contando como existencia: siguen en el almacén.
        </p>
      )}
      {/* El fallo de carga lo pinta la lista (motivo en español + reintentar). */}
    </>
  );
}
